import SuperFeedbackCertificate from './SuperFeedbackCertificate';
import Task from './Task';
import { useState, useEffect } from 'react';
import Task2FeedbackPopup from './Task2FeedbackPopup';
import {
  useAddTask2FeedbackMutation,
  useGetTask2FeedbackStatusQuery,
} from '../superCapstoneSlice';

interface allProps {
  data: any;
  setSelectedTask: Function;
  enrollData: any;
  setCount: Function;
  isEnrolled: boolean;
  userProgress: any;
  setShowFeedback: any;
}

const Tasks = (props: allProps) => {
  // Sort the tasks according to the taskNo before displaying
  let tasks = props.data?.tasks ? [...props.data.tasks] : []; // Fix: Handle potential undefined tasks safely
  tasks.sort((a: any, b: any) => {
    return a.taskNo - b.taskNo;
  });
  const superCapstoneId = props.data._id;

  // Get enrollId from enrollData
  const enrollId = props.enrollData?._id || '';

  const [showTask2Feedback, setShowTask2Feedback] = useState(false);
  const [task2DetailsForFeedback, setTask2DetailsForFeedback] =
    useState<any>(null);
  const [task2FeedbackSubmitted, setTask2FeedbackSubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');

  // API mutation hook
  const [addTask2Feedback, { isLoading: isSubmittingFeedback }] =
    useAddTask2FeedbackMutation();

  // Get task2 feedback status from API
  const {
    data: feedbackStatusData,
    isSuccess: feedbackStatusSuccess,
    isLoading: feedbackStatusLoading,
  } = useGetTask2FeedbackStatusQuery({ enrollId }, { skip: !enrollId });

  // Update local state when API response is received
  useEffect(() => {
    if (feedbackStatusSuccess && feedbackStatusData) {
      setTask2FeedbackSubmitted(feedbackStatusData.hasSubmitted || false);
    }
  }, [feedbackStatusSuccess, feedbackStatusData]);

  // Check if the task has been completed
  const isCompleted = (currentTaskNo: number): boolean => {
    // Find the task/quiz corresponding to the currentTaskNo.
    // Check whether the quiz has been meets the percentage requirements or the totalAttempts have been used up.
    let currentTaskDone = false;
    let currentQuizDone = false;

    // Ensure enrollData and its properties exist before accessing them
    const enrolledTasks = props.enrollData?.tasks || [];
    const enrolledQuizzes = props.enrollData?.quiz || [];

    for (const task of enrolledTasks) {
      if (task.taskNo === currentTaskNo) {
        currentTaskDone = true;
        break;
      }
    }
    for (const quiz of enrolledQuizzes) {
      if (quiz.taskNo === currentTaskNo) {
        // If quiz completed or attempts used up
        if (quiz.percentage >= 40 || quiz.totalAttempt > 2) {
          currentQuizDone = true;
        }
        break;
      }
    }

    return currentTaskDone && currentQuizDone;
  };

  // Determine if all tasks are completed
  const allTasksCompleted =
    props.isEnrolled &&
    tasks.length > 0 &&
    tasks.every((task) => isCompleted(task.taskNo));

  // Function to open the feedback popup for task 2
  const handleOpenTask2Feedback = (taskDetail: any) => {
    if (taskDetail.taskNo === 2) {
      if (!task2FeedbackSubmitted) {
        // Open only if not already submitted
        setTask2DetailsForFeedback(taskDetail);
        setShowTask2Feedback(true);
        setFeedbackError(''); // Clear any previous errors
      }
    }
  };

  const handleCloseTask2Feedback = () => {
    setShowTask2Feedback(false);
    setTask2DetailsForFeedback(null);
    setFeedbackError('');
  };

  const handleSubmitTask2Feedback = async (feedback: {
    q1: string;
    q2: string;
    q2Explanation: string;
    q3: string;
    q3Explanation: string;
  }) => {
    if (!enrollId) {
      setFeedbackError('Unable to submit feedback: enrollment data not found');
      return;
    }

    try {
      // Prepare data for API
      const payload = {
        enrollId: enrollId,
        ...feedback,
      };

      // Call API
      await addTask2Feedback(payload).unwrap();

      console.log(
        'Task 2 Feedback Submitted:',
        feedback,
        'for task:',
        task2DetailsForFeedback?.title
      );

      // Mark as submitted to prevent re-opening for automatic popup
      setTask2FeedbackSubmitted(true);

      // Close the popup upon successful submission
      handleCloseTask2Feedback();
    } catch (error: any) {
      console.error('Failed to submit feedback:', error);
      setFeedbackError(
        error?.data?.message ||
          'An error occurred while submitting feedback. Please try again.'
      );
    }
  };

  return (
    <div className=''>
      {tasks.length > 0 && (
        <table className='w-full'>
          <thead className=''>
            <tr className='bg-slate-100 '>
              <th className='border border-slate-300 font-normal text-left p-2 md:px-4 '>
                Task Name
              </th>
              <th className='border border-slate-300 font-normal text-left p-2 md:px-4'>
                Hours
              </th>
              <th className='border border-slate-300 font-normal text-left p-2 md:px-4'>
                Skills
              </th>
              <th className='border border-slate-300 font-normal text-left p-2 md:px-4'>
                Status
              </th>
            </tr>
          </thead>

          <tbody>
            {tasks.map((task: any, index: number) => (
              <Task
                // If not enrolled, lock everything
                // if first task, dont lock
                // If prevTasks and quiz done dont lock
                isLocked={
                  props.isEnrolled
                    ? task.taskNo === 1
                      ? false
                      : !isCompleted(task.taskNo - 1!)
                    : true
                }
                isCompleted={
                  props.isEnrolled ? isCompleted(task.taskNo) : false
                }
                taskDetail={task}
                key={index}
                setSelectedTask={props.setSelectedTask}
                setCount={() => {
                  props.setCount(index + 1);
                }}
                superCapstoneId={superCapstoneId}
                taskNo={task.taskNo}
                openTask2FeedbackPopup={handleOpenTask2Feedback}
                isTask2FeedbackSubmitted={task2FeedbackSubmitted}
              />
            ))}
            <SuperFeedbackCertificate
              enrollData={props.enrollData}
              capstoneData={props.data}
              userProgress={props.userProgress}
              setShowFeedback={props.setShowFeedback}
              allTasksCompleted={allTasksCompleted}
            />
          </tbody>
        </table>
      )}
      {/* Render the Task 2 Feedback Popup */}
      {task2DetailsForFeedback &&
        !task2FeedbackSubmitted &&
        !feedbackStatusLoading && (
          <Task2FeedbackPopup
            isOpen={showTask2Feedback}
            onClose={handleCloseTask2Feedback}
            onSubmit={handleSubmitTask2Feedback}
            taskTitle={task2DetailsForFeedback.title}
            error={feedbackError}
            isSubmitting={isSubmittingFeedback}
          />
        )}
    </div>
  );
};

export default Tasks;
